import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiArrowRight, HiChevronDown } from "react-icons/hi2";
import { NiggleLog, PerformanceLog, TrainingLog } from "../../types/post";
import { PeakEvent } from "../../types/event";
import { useAuth } from "../../contexts/AuthContext";
import LogCard from "../shared/LogCard";
import {
  LogCardChip,
  LogCardMetric,
  LogCardMetricsRow,
  LogCardNote,
} from "../shared/LogCardContent";
import PerformanceIntentBadge from "../shared/PerformanceIntentBadge";
import { formatResultVariable } from "../../utils/performanceFormat";
import { linkPerformanceToEvent } from "../../services/performanceService";
import LinkToEventDialog from "./performance/LinkToEventDialog";

interface PerformanceCardProps {
  log: TrainingLog & { id: string };
  /** Results whose linkedTrainingLogID points at this log (folded in). */
  results: (PerformanceLog & { id: string })[];
  /** Niggles linked to this log. */
  niggles?: (NiggleLog & { id: string })[];
  /** The diary owner's events (event name lookup via `log.eventId`). */
  events: PeakEvent[];
  /** Owner of the log; enables the "Link to event" / "Unlink" actions. */
  ownerUserId?: string;
  /** Called after a link / unlink so the parent can refresh. */
  onLinkChanged?: () => void;
  /** Resolved training type name (by trainingTypeID) when known. */
  trainingTypeName?: string | null;
  onEdit?: () => void;
  onDelete?: () => void;
}

/** Event name shown on a Performance card (looked up by `eventId`). */
export function performanceEventName(
  log: Pick<TrainingLog, "eventId">,
  events: PeakEvent[],
): string | null {
  if (!log.eventId) return null;
  return events.find((e) => e.id === log.eventId)?.name?.trim() || null;
}

/**
 * Diary card for a training log with `performanceType` (PerformanceLog.md
 * §5.1): intent badge, event, load / RPE / duration / type, result rows with
 * PB / SB marks, niggle chip and notes. Its result logs are folded
 * in and not rendered as separate cards.
 */
const PerformanceCard: React.FC<PerformanceCardProps> = ({
  log,
  results,
  niggles = [],
  events,
  ownerUserId,
  onLinkChanged,
  trainingTypeName,
  onEdit,
  onDelete,
}) => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const eventName = performanceEventName(log, events);
  const event = log.eventId ? events.find((e) => e.id === log.eventId) : undefined;
  const canLink = !!ownerUserId;

  const handleUnlink = async () => {
    if (!ownerUserId) return;
    setUnlinking(true);
    try {
      await linkPerformanceToEvent(ownerUserId, log.id, null);
      onLinkChanged?.();
    } catch (error) {
      console.error("Error unlinking performance:", error);
    } finally {
      setUnlinking(false);
    }
  };
  const typeName =
    trainingTypeName ?? (log.tag && log.tag.length > 0 ? log.tag[0] : null);

  return (
    <div
      onClick={() => {
        if (userData?.casualMode) return;
        setIsExpanded((prev) => !prev);
      }}
      className={userData?.casualMode ? "" : "cursor-pointer"}
    >
      <div className="relative">
        {!onEdit && !onDelete && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/insights/trainingLog/${log.id}`);
            }}
            className="absolute top-2 right-2 z-10 p-1.5 hover:bg-base-200 rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            aria-label="View performance details"
          >
            <HiArrowRight className="w-3.5 h-3.5 text-base-content/50" />
          </button>
        )}

        <LogCard
          className="bg-base-200 border border-base-300"
          onEdit={onEdit}
          onDelete={onDelete}
        >
          {/* Header row */}
          <div className="flex items-center gap-2 pr-8 flex-wrap">
            <PerformanceIntentBadge type={log.performanceType} />
            {log.duration > 0 && (
              <span className="text-xs text-base-content/70 font-geist">
                {log.duration}min
              </span>
            )}
            {event?.endDate && event.endDate !== event.date && log.eventDay && (
              <span className="text-xs text-base-content/60 font-geist">
                · Day {log.eventDay}
              </span>
            )}
            {!userData?.casualMode && (
              <HiChevronDown
                className={`w-3 h-3 text-base-content/30 transition-transform duration-200 ml-auto mr-4 ${
                  isExpanded ? "rotate-180" : ""
                }`}
              />
            )}
          </div>

          {/* Event name */}
          {eventName && (
            <p className="text-sm font-semibold text-base-content font-geist mt-1 truncate">
              {eventName}
            </p>
          )}

          {/* Type + load summary chips */}
          <div className="mt-1.5">
            <LogCardMetricsRow gap={1}>
              {typeName && <LogCardChip label={typeName} />}
              <LogCardChip label={`Load ${log.load}`} />
              <LogCardChip label={`RPE ${log.rpe}`} />
              {niggles.length > 0 && (
                <LogCardChip
                  label={`Niggle · pain ${niggles[0].pain}/10`}
                  variant="warning"
                />
              )}
            </LogCardMetricsRow>
          </div>

          {/* Result rows */}
          {results.length > 0 && (
            <div className="mt-2 space-y-1">
              {results.map((result) => {
                const measured = (result.activity?.variables ?? [])
                  .filter((v) => v.measurement != null)
                  .map(formatResultVariable);
                return (
                  <div
                    key={result.id}
                    className="flex items-start justify-between gap-2 text-sm font-geist"
                  >
                    <div className="min-w-0">
                      <span className="font-medium text-base-content">
                        {result.activity?.activityName ||
                          result.performanceTitle ||
                          "Result"}
                      </span>
                      {measured.length > 0 && (
                        <span className="text-base-content/70">
                          : {measured.join(" · ")}
                        </span>
                      )}
                      {(result.tag ?? [])
                        .filter((t) => t === "PB" || t === "SB")
                        .map((t) => (
                          <span
                            key={t}
                            className="ml-1.5 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-700 font-geist"
                          >
                            {t}
                          </span>
                        ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Notes */}
          {log.notes && <LogCardNote text={log.notes} />}

          {/* Link to / unlink from an event (Events.md §3.4) */}
          {canLink && (
            <div className="mt-2 flex items-center gap-3">
              {log.eventId ? (
                <button
                  type="button"
                  disabled={unlinking}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUnlink();
                  }}
                  className="text-xs text-base-content/60 hover:text-base-content font-grotesk min-h-9 disabled:opacity-50"
                >
                  {unlinking ? "Unlinking..." : "Unlink from event"}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLinkOpen(true);
                  }}
                  className="text-xs text-primary hover:underline font-grotesk min-h-9"
                >
                  Link to event
                </button>
              )}
            </div>
          )}

          {/* Expanded details */}
          {!userData?.casualMode && isExpanded && (
            <div className="mt-3 space-y-1.5">
              <LogCardMetric value={log.load} label="Load" />
              <LogCardMetric value={log.rpe} label="RPE" />
              <LogCardMetric value={`${log.duration}min`} label="Duration" />
              <LogCardMetric
                value={`${log.muscular} / ${log.respiratory}`}
                label="Muscular / Respiratory"
              />
              {log.trainingPerformed !== undefined && (
                <LogCardMetric
                  value={log.trainingPerformed}
                  label="Performance rating"
                />
              )}
              {results.map((result) => (
                <div
                  key={`${result.id}-detail`}
                  className="bg-base-200 rounded-lg p-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold tracking-wide uppercase text-base-content/60 font-geist">
                      {result.activity?.activityName || "Result"}
                    </span>
                    <span className="text-[11px] font-geist text-base-content/70">
                      Rating {result.performanceExpectation}/10
                    </span>
                  </div>
                  {(result.activity?.variables ?? []).length > 0 && (
                    <div className="mt-1.5 space-y-0">
                      {(result.activity?.variables ?? []).map((v, i) => (
                        <div
                          key={i}
                          className="flex justify-between items-center text-xs py-1.5 border-b border-base-300/50 last:border-0"
                        >
                          <span className="text-base-content/60 font-geist">
                            {v.name}
                          </span>
                          <span className="font-bold text-base-content font-geist">
                            {v.measurement != null
                              ? formatResultVariable(v)
                              : "\u2014"}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {result.notes && <LogCardNote text={result.notes} />}
                </div>
              ))}
              {niggles.map((n) => (
                <div key={n.id} className="bg-base-200 rounded-lg p-3">
                  <span className="text-[11px] font-semibold tracking-wide uppercase text-base-content/60 font-geist">
                    Niggle
                  </span>
                  <p className="text-xs text-base-content/70 font-geist mt-1">
                    Pain {n.pain}/10 · Impact {n.impact}/10
                  </p>
                  {n.notes && <LogCardNote text={n.notes} />}
                </div>
              ))}
            </div>
          )}
        </LogCard>
      </div>
      {ownerUserId && (
        <LinkToEventDialog
          isOpen={linkOpen}
          onClose={() => setLinkOpen(false)}
          userId={ownerUserId}
          trainingLogId={log.id}
          dateID={log.dateID}
          onLinked={() => onLinkChanged?.()}
        />
      )}
    </div>
  );
};

export default PerformanceCard;
